import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { browserHistory } from "react-router";

import { setAccessToken, setUserType } from "utils/helpers";
import LoadingProgress from "components/LoadingProgress";
import SnackbarError from "components/SnackbarError";
import Header from "components/Header";
import RSLeftMenu from "components/RSLeftMenu";

import {
  loadInitialData,
  resetError,
  ttloggerEventLog,
  setLocation,
  ttloggerUserID,
} from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectCurrentUser,
  makeSelectRouteQuery,
  makeSelectCategories,
  makeSelectProfiles,
  makeSelectErrorMsg,
} from "./selectors";
import { changeLocale } from "../LanguageProvider/actions";
import { makeSelectLocale } from "../LanguageProvider/selectors";

const styles = {
  main: {
    paddingTop: 64,
    flex: "1 0 auto",
  },
  wrapperFlex: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  backdrop: {
    zIndex: 1400,
    backgroundColor: "rgba(0, 0, 0, 0.541176)",
    position: "fixed",
    top: 64,
    left: 0,
    right: 0,
    bottom: 0,
  },
};

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.showLeftMenu = this.showLeftMenu.bind(this);
    this.setLeftMenuVisible = this.setLeftMenuVisible.bind(this);
    this.onMenuItemSelected = this.onMenuItemSelected.bind(this);
    this.onPopoverVisible = this.onPopoverVisible.bind(this);
    this.onChangeLanguage = this.onChangeLanguage.bind(this);

    this.state = {
      leftMenuVisible: false,
      backdropVisible: false,
    };
  }

  componentWillMount() {
    const {
      dispatchLoadInitialData,
      location: { query },
    } = this.props;
    // To test in development query.access_token also supporting previous version (token in url param)
    // New version getting the token from localStorage Injected from Mobile RewardsWebView
    const accessToken =
      query.access_token || localStorage.getItem("injectedToken");
    const inAsAnon = query.InAsAnon;
    if (accessToken) {
      setAccessToken(accessToken);
      setUserType(inAsAnon);
    }
    dispatchLoadInitialData();
  }

  setLocation = (position) => {
    this.props.dispatchSetLocation(
      position.coords.latitude,
      position.coords.longitude
    );
  };

  getCurrentCategoryId() {
    const { categoryId } = this.props;
    if (categoryId) {
      return parseInt(categoryId, 10);
    }
    return null;
  }

  showLeftMenu() {
    this.setLeftMenuVisible(true);
  }

  setLeftMenuVisible(visible) {
    this.setState({
      leftMenuVisible: visible,
    });
  }

  onMenuItemSelected(value) {
    this.setLeftMenuVisible(false);
    if (/^(mailto|http)/i.test(value)) {
      window.open(value, "_blank");
    } else {
      browserHistory.push(value);
    }
  }

  onPopoverVisible(visible) {
    this.setState({
      backdropVisible: visible,
    });
  }
  onChangeLanguage(value) {
    const { dispatchSetLocale } = this.props;
    dispatchSetLocale(value);
  }

  // /////////////////
  // render

  render() {
    const { children } = this.props;
    const {
      location: { pathname },
    } = this.props;
    const resetPasswordPathnameList = [
      "/reset_password",
      "/reset_password_successful",
    ];
    const isResetPasswordPageActive =
      resetPasswordPathnameList.includes(pathname);
    return (
      <div style={styles.wrapperFlex}>
        {!isResetPasswordPageActive && this.renderHeader()}
        {this.renderLeftMenu()}
        <main style={styles.main}>
          {children}
          {this.renderLoading()}
          {this.renderBackdrop()}
          {this.renderError()}
        </main>
      </div>
    );
  }

  renderHeader() {
    const {
      user,
      categories,
      location: { pathname },
      locale,
    } = this.props;
    const categoryId = this.getCurrentCategoryId();
    return (
      <Header
        user={user}
        categories={categories}
        categoryId={categoryId}
        pathname={pathname}
        onPopoverVisible={this.onPopoverVisible}
        showLeftMenu={this.showLeftMenu}
        onMenuItemSelected={this.onMenuItemSelected}
        onTTloggerEventLog={this.props.onTTloggerEventLog}
        onChangeLanguage={this.onChangeLanguage}
        locale={locale}
      />
    );
  }

  renderLeftMenu() {
    const { categories } = this.props;
    if (!categories) {
      return;
    }
    const { user, profiles } = this.props;
    const { leftMenuVisible } = this.state;
    return (
      <RSLeftMenu
        user={user}
        categories={categories}
        profiles={profiles}
        visible={leftMenuVisible}
        onVisibleChange={this.setLeftMenuVisible}
        onMenuItemSelected={this.onMenuItemSelected}
      />
    );
  }

  renderBackdrop() {
    const { backdropVisible } = this.state;
    if (backdropVisible) {
      return <div style={styles.backdrop} />;
    }
  }

  renderLoading() {
    const { loading } = this.props;
    if (!loading) {
      return;
    }
    return <LoadingProgress />;
  }

  renderError() {
    const { error, customErrorMsg, onErrorRequestClose } = this.props;
    return (
      <SnackbarError
        error={error}
        customErrorMsg={customErrorMsg}
        onClose={onErrorRequestClose}
      />
    );
  }
}

App.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  customErrorMsg: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dispatchLoadInitialData: PropTypes.func.isRequired,
  dispatchSetLocale: PropTypes.func.isRequired,
  onTTloggerEventLog: PropTypes.func.isRequired,
  onTTloggerUserId: PropTypes.func.isRequired,
  dispatchSetLocation: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  categories: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  profiles: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  categoryId: PropTypes.string,
  onErrorRequestClose: PropTypes.func,
  location: PropTypes.object,
  children: PropTypes.node,
  locale: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  customErrorMsg: makeSelectErrorMsg(),
  user: makeSelectCurrentUser(),
  categories: makeSelectCategories(),
  profiles: makeSelectProfiles(),
  categoryId: makeSelectRouteQuery("category"),
  locale: makeSelectLocale(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatchLoadInitialData: () => {
      dispatch(loadInitialData());
    },
    onErrorRequestClose: () => {
      dispatch(resetError());
    },
    onTTloggerEventLog: (name, event) => {
      dispatch(ttloggerEventLog(name, event));
    },
    onTTloggerUserId: (user) => {
      dispatch(ttloggerUserID(user));
    },
    dispatchSetLocation: (latitude, longitude) => {
      dispatch(setLocation(latitude, longitude));
    },
    dispatchSetLocale: (locale) => {
      dispatch(changeLocale(locale));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
